import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Neutrals } from '../../color-palette/colors';
import { FeatureErrorState } from '../FeatureErrorBoundary';
import { visuallyHiddenSx } from '../Fields/fieldStyles';

import { fillColumnSx, PROSE_MAX } from './screenLayout';
import { StaleNotice } from './StaleNotice';
import { StateArea } from './StateArea';
import type { StatePlacement } from './StateArea';

const pausedSx = { color: Neutrals[600], maxWidth: PROSE_MAX } as const;

const PausedNote: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Typography variant="caption2" sx={pausedSx}>
      {t('common.waitingForConnection', 'Waiting for the connection to come back…')}
    </Typography>
  );
};

export interface LoadingStateProps {
  label: string;
  paused?: boolean;
  fallback?: React.ReactNode;
  placement?: StatePlacement;
}

const spinnerStackSx = { py: 8 } as const;

export const LoadingState: React.FC<LoadingStateProps> = ({ label, paused = false, fallback, placement = 'content' }) => {
  if (fallback !== undefined && fallback !== null) {
    return (
      <Stack spacing={3}>
        <Box component="span" sx={visuallyHiddenSx}>
          {label}
        </Box>
        {paused && <PausedNote />}
        {fallback}
      </Stack>
    );
  }
  return (
    <StateArea placement={placement}>
      <Stack spacing={3} alignItems="center" sx={spinnerStackSx}>
        <CircularProgress aria-label={label} />
        {paused && <PausedNote />}
      </Stack>
    </StateArea>
  );
};

export const SilentLoadingState: React.FC = () => (
  <StateArea>
    <Stack alignItems="center" sx={spinnerStackSx}>
      <CircularProgress aria-hidden="true" />
    </Stack>
  </StateArea>
);

export const PageLoading: React.FC = () => {
  const { t, ready } = useTranslation(undefined, { useSuspense: false });
  if (!ready) return <SilentLoadingState />;
  return <LoadingState label={t('common.loadingPage', 'Loading the page')} />;
};

export interface LoadErrorStateProps {
  title: string;
  reason?: string | null;
  retrying?: boolean;
  onRetry: () => void;
  placement?: StatePlacement;
}

export const LoadErrorState: React.FC<LoadErrorStateProps> = ({ title, reason, retrying = false, onRetry, placement = 'content' }) => {
  const { t } = useTranslation();
  return (
    <StateArea placement={placement}>
      <FeatureErrorState
        align="center"
        title={title}
        body={reason ?? undefined}
        retryLabel={t('common.tryAgain', 'Try again')}
        retrying={retrying}
        onRetry={onRetry}
      />
    </StateArea>
  );
};

export interface LoadState {
  hasData: boolean;
  isError: boolean;
  isPaused: boolean;
  isRetrying: boolean;
  isStale?: boolean;
  loadError: unknown;
  refetch: () => void;
}

export interface ScreenBodyProps {
  load: LoadState;
  errorTitle: string;
  loadingLabel: string;
  errorReason?: (error: unknown) => string | null;
  loadingFallback?: React.ReactNode;
  fill?: boolean;
  placement?: StatePlacement;
  showStale?: boolean;
  children: React.ReactNode;
}

const LoadedOr: React.FC<Omit<ScreenBodyProps, 'fill'>> = ({
  load,
  errorTitle,
  loadingLabel,
  errorReason,
  loadingFallback,
  placement,
  children,
}) => {
  if (load.hasData) return <>{children}</>;
  if (load.isError) {
    return (
      <LoadErrorState
        title={errorTitle}
        reason={errorReason?.(load.loadError) ?? null}
        retrying={load.isRetrying}
        onRetry={load.refetch}
        placement={placement}
      />
    );
  }
  return <LoadingState label={loadingLabel} paused={load.isPaused} fallback={loadingFallback} placement={placement} />;
};

const bodySx = { minWidth: 0 } as const;
const filledBodySx = [bodySx, fillColumnSx] as const;
const staleNoticeSx = { marginBlockEnd: 4 } as const;

const showsCentredState = ({ load, loadingFallback }: Pick<ScreenBodyProps, 'load' | 'loadingFallback'>): boolean =>
  !load.hasData && (load.isError || loadingFallback === undefined || loadingFallback === null);

const showsStaleNotice = (load: LoadState): boolean => load.hasData && load.isStale === true;

export const ScreenBody: React.FC<ScreenBodyProps> = ({ fill = false, showStale = false, ...props }) => (
  <Box aria-busy={!props.load.hasData && !props.load.isError} sx={fill || showsCentredState(props) ? filledBodySx : bodySx}>
    {showStale && showsStaleNotice(props.load) && <StaleNotice onRetry={props.load.refetch} sx={staleNoticeSx} />}
    <LoadedOr {...props} />
  </Box>
);
